<script setup lang="ts">
import type { PageSettings } from '~/layers/cms/app/types/cmsTypes'
import { useAuthStore } from '~/layers/cms/app/stores/authStore'
import { computed, onMounted, ref } from 'vue'
import { useFetch, useRoute, useRuntimeConfig } from '#app'
import AppButton2 from '~/components/AppButton2.vue'
import AppButton3 from '~/components/AppButton3.vue'
import { getNewsList } from '#imports'

const config = useRuntimeConfig()
const { data } = await useFetch(`${config.public.apiBase}/api/get-news-list`, {
  transform: response => (Array.isArray(response) ? (response as PageSettings[]) : []),
  getCachedData: (key, nuxtApp) =>
    nuxtApp.isHydrating ? nuxtApp.payload.data[key] : undefined
})
const authStore = useAuthStore()
const newsList = ref<PageSettings[]>(data.value ?? [])
onMounted(async () => {
  if (authStore.authorized) {
    const res = await getNewsList()
    if (res.status_ok) {
      newsList.value = res.data as PageSettings[]
    }
  }
})

const aktualnosci = computed<PageSettings[]>(() => {
  return newsList.value ?? []
})

const route = useRoute()

const currentIndex = computed(() =>
  aktualnosci.value.findIndex(x => x.slug === route.path)
)

const currentAktualnosc = computed(() =>
  currentIndex.value === -1 ? undefined : aktualnosci.value[currentIndex.value]
)

const prevSlug = computed(() => {
  const arr = aktualnosci.value
  if (!arr.length || currentIndex.value === -1) return undefined
  return arr[(currentIndex.value - 1 + arr.length) % arr.length]?.slug
})

const nextSlug = computed(() => {
  const arr = aktualnosci.value
  if (!arr.length || currentIndex.value === -1) return undefined
  return arr[(currentIndex.value + 1) % arr.length]?.slug
})
</script>

<template>
  <div>
    <div class="h-18 xl:h-31.5" />
    <div class="h-12 xl:h-24" />
    <div class="flex flex-col gap-20">
      <div class="xl:grid grid-cols-1 xl:grid-cols-12 grid-rows-1 px-2xl gap-14">
        <div class="hidden xl:block px-14 col-span-2">
          <AppButton3
            :arrow-left="true"
            @click="$router.back()"
          />
        </div>

        <div class="flex flex-col gap-10 w-full col-span-8">
          <div class="flex flex-col gap-2.5">
            <span class="text-H2 text-Tekst">{{ currentAktualnosc?.customData.articleTitle ?? 'Tytuł aktualności' }}</span>
            <span class="text-Paragraph text-Tekst">{{ currentAktualnosc?.customData.articleDate ?? 'Data aktualności' }}</span>
          </div>
          <NuxtImg
            :src="(currentAktualnosc?.customData?.articleImage as string)"
            width="1220"
            height="690"
            format="webp"
            quality="100"
            alt="aktualność"
            loading="eager"
            class="rounded-3xl"
          />
          <slot />
        </div>
        <div class="col-span-2" />
      </div>
      <div class="px-2xl">
        <div class="flex flex-row justify-between xl:justify-end items-center gap-2 xl:gap-15">
          <AppButton2
            :href="prevSlug"
            :arrow-left="true"
          >
            <span class="text-Menu">Poprzedni wpis</span>
          </AppButton2>
          <AppButton2 :href="nextSlug">
            <span class="text-Menu">Następny wpis</span>
          </AppButton2>
        </div>
      </div>
      <CmsComponentsListBlock :cms-data-path="['data2']" />
    </div>
  </div>
</template>
